import { MultiTextInput } from 'components/TagsInput'
import { Switch, TextField } from 'formik-mui'
import { isEmpty } from 'lodash'
import React, { Suspense } from 'react'
import { HtmlEditorField, NumberField } from 'scenes/Dashboard/components-next/Forms/Controllers'
import { FundingRoundList } from 'scenes/Dashboard/components-next/FundingRounds'
import { LoadingScreen } from 'scenes/Dashboard/components-next/Loading'
import { FormSection } from 'scenes/Dashboard/components-next/Sections'
import { AutoCompleteDataType, AutoCompleteFormik, SelectFormik } from 'scenes/Dashboard/components/Inputs/Select'
import {
  Organization,
  OrganizationSourceOptions,
  OrganizationStatusOptions,
  OrganizationTypeOptions,
} from 'scenes/Dashboard/data/organizations/types'
import { FastField, Field, FormBag, FormProps, withForm } from 'utils-next/forms'
import * as Yup from 'yup'

import { ButtonBase, createStyles, Fade, FormControlLabel, Theme, Typography } from '@mui/material'
import { WithStyles, withStyles } from '@mui/styles'

import AttachmentList from './components/Attachments/AttachmentList'
import OrganizationEventsEditList from './components/OrganizationEvents/OrganizationEventsEditList.component'
import PerformanceIndicatorEditList from './components/PerformanceIndicators/PerformanceIndicatorsEditList.component'
import { signalAssessmentValidationSchema } from './components/Signals/Signal.component'
import SignalList from './components/Signals/SignalList.component'
import { OrganizationFormValues } from './OrganizationDetailsEdit.models'

class OrganizationDetailsEdit extends React.PureComponent<
  OrganizationDetailsFullEditProps & WithStyles<typeof styles>,
  OrganizationDetailsEditState
> {
  constructor(props: OrganizationDetailsFullEditProps & WithStyles<typeof styles>) {
    super(props)
    this.extractDomainFromWebsite = this.extractDomainFromWebsite.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
  }

  componentDidUpdate(prevProps: OrganizationDetailsFullEditProps) {
    const { setSubmitting } = this.props
    if (!prevProps.submitting && this.props.submitting && !this.props.isSubmitting) {
      this.handleSubmit()
    } else if (prevProps.submitting && !this.props.submitting && this.props.isSubmitting) {
      setSubmitting(false)
    }
  }

  async handleSubmit() {
    const { handleSubmit, values, validateForm, onCancel } = this.props
    const errorMap = await validateForm(values)
    if (isEmpty(errorMap)) {
      handleSubmit()
    } else {
      onCancel()
    }
  }

  extractDomainFromWebsite() {
    const { websiteUrlValue } = this.props.values
    const { setFieldValue } = this.props
    if (!websiteUrlValue) {
      return
    }
    try {
      const hostname = new URL(websiteUrlValue).host.replace('www.', '')
      setFieldValue('domainValue', hostname)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(`Invalid website URL ${websiteUrlValue}`)
    }
  }

  render() {
    const {
      classes,
      isSubmitting,
      values: {
        organizationId,
        foundersFullTimeValue,
        experienceInIndustryValue,
        serialEntrepreneursValue,
        communicateRoundClosedValue,
        founders,
      },
    } = this.props

    return (
      <Fade in={true}>
        <form>
          <FormControlLabel
            control={<Field type="checkbox" name="processedByOpsValue" component={Switch} color="secondary" />}
            label="Manually edited"
          />

          <FastField
            component={TextField}
            label="Name"
            name="nameValue"
            disabled={isSubmitting}
            className={classes.field}
            autoFocus
            fullWidth
          />

          <SelectFormik
            name="typesValue"
            label="Types"
            options={OrganizationTypeOptions}
            disabled={isSubmitting}
            className={classes.field}
            isMulti
          />

          <SelectFormik
            name="sourceValue"
            label="Source"
            options={OrganizationSourceOptions}
            disabled={isSubmitting}
            className={classes.field}
          />

          <FastField
            component={TextField}
            label="Website URL"
            name="websiteUrlValue"
            disabled={isSubmitting}
            className={classes.field}
            fullWidth
          />

          <FastField component={TextField} label="Domain URL" name="domainValue" disabled={isSubmitting} fullWidth />

          <ButtonBase className={classes.extractFromUrl} onClick={this.extractDomainFromWebsite}>
            <Typography color="secondary">Extract from website URL</Typography>
          </ButtonBase>

          <FastField
            component={TextField}
            label="Linkedin URL"
            name="linkedinUrlValue"
            disabled={isSubmitting}
            className={classes.field}
            fullWidth
          />

          <FastField
            component={TextField}
            label="Logo URL"
            name="logoUrlValue"
            disabled={isSubmitting}
            className={classes.field}
            fullWidth
          />

          <FastField
            component={TextField}
            label="Blurb"
            name="blurbValue"
            disabled={isSubmitting}
            className={classes.field}
            multiline
            fullWidth
          />

          <AutoCompleteFormik
            label="Headquarters"
            dataType={AutoCompleteDataType.locations}
            name="headquarters"
            disabled={isSubmitting}
            className={classes.field}
            isMulti
          />

          <AutoCompleteFormik
            label="Operating locations"
            dataType={AutoCompleteDataType.locations}
            name="operatingLocations"
            disabled={isSubmitting}
            className={classes.field}
            isMulti
          />

          <AutoCompleteFormik
            label="Industries"
            dataType={AutoCompleteDataType.adminIndustries}
            name="industryValues"
            disabled={isSubmitting}
            className={classes.field}
            isMulti
          />

          <FastField
            component={TextField}
            label="Founded On Date"
            name="foundedOnValue"
            type="date"
            disabled={isSubmitting}
            className={classes.field}
            InputLabelProps={{
              shrink: true,
            }}
            fullWidth
          />

          <SelectFormik
            name="statusValue"
            label="Status"
            options={OrganizationStatusOptions}
            disabled={isSubmitting}
            className={classes.field}
          />

          <FormControlLabel
            control={
              <FastField
                type="checkbox"
                name="communicateRoundClosedValue"
                value={communicateRoundClosedValue}
                component={Switch}
                color="secondary"
              />
            }
            className={classes.field}
            label="Communicate Round Closed"
          />

          {communicateRoundClosedValue && (
            <FastField
              component={HtmlEditorField}
              label="Round Closed Text"
              name="communicateRoundClosedExplanationValue"
              disabled={isSubmitting}
              helperText="Please, limit this description to 150 characters"
              className={classes.field}
              maxLength={150}
            />
          )}

          <FastField
            component={TextField}
            label="Reported likelihood of being fundraising"
            name="likelihoodValue"
            type="date"
            disabled={isSubmitting}
            className={classes.field}
            InputLabelProps={{
              shrink: true,
            }}
            fullWidth
          />

          <FastField
            component={TextField}
            label="Reported Stage announced on"
            name="currentRoundAnnouncedOn"
            type="date"
            disabled={isSubmitting}
            className={classes.field}
            InputLabelProps={{
              shrink: true,
            }}
            fullWidth
          />

          <MultiTextInput
            label="Organization Referral"
            name="referrersValue"
            placeholder="john.doe@mail.com, investor.com..."
            InputLabelProps={{
              shrink: true,
            }}
            disabled={isSubmitting}
            className={classes.field}
            fullWidth
          />

          <FastField
            component={TextField}
            label="Insights"
            name="insightsValue"
            disabled={isSubmitting}
            className={classes.field}
            multiline
            fullWidth
          />

          <FastField
            component={TextField}
            label="Description"
            name="descriptionValue"
            disabled={isSubmitting}
            className={classes.field}
            multiline
            fullWidth
          />

          <FastField
            component={TextField}
            label="Rejected Investors"
            name="rejectedInvestorsValue"
            className={classes.field}
            disabled={isSubmitting}
            multiline
            fullWidth
          />

          <FastField
            component={TextField}
            label="Investors in Cap Table"
            name="investorsInCapTableValue"
            className={classes.field}
            disabled={isSubmitting}
            multiline
            fullWidth
          />

          <AutoCompleteFormik
            label="Last Round type"
            dataType={AutoCompleteDataType.roundType}
            name="lastRoundTypeValue"
            disabled={isSubmitting}
            className={classes.field}
          />

          <FastField
            component={NumberField}
            label="Total Funding Raised"
            adornmentText="USD"
            name="totalFundingRaisedValue"
            disabled={isSubmitting}
            className={classes.field}
            fullWidth
            internalInputProps={{ decimalScale: 0 }}
            InputLabelProps={{
              shrink: true,
            }}
          />

          <FastField
            component={TextField}
            label="Last Round Closed On"
            name="lastRoundClosedOnValue"
            type="date"
            disabled={isSubmitting}
            className={classes.field}
            InputLabelProps={{
              shrink: true,
            }}
            fullWidth
          />

          <FastField
            component={TextField}
            label="Founder Organization Notes"
            name="founderNotesValue"
            className={classes.field}
            disabled={isSubmitting}
            multiline
            fullWidth
          />

          <FormControlLabel
            control={<FastField type="checkbox" name="foundersFullTimeValue" component={Switch} color="secondary" />}
            label="Founders are full time"
          />

          {!foundersFullTimeValue && (
            <Field
              component={TextField}
              label="Explain why founders are not full time"
              name="foundersFullTimeExplanationValue"
              disabled={isSubmitting}
              multiline
              fullWidth
            />
          )}

          <FormControlLabel
            control={<Field type="checkbox" name="experienceInIndustryValue" component={Switch} color="secondary" />}
            label="Founders have experience in the industry"
          />

          {experienceInIndustryValue && (
            <Field
              component={TextField}
              label="Explain the experience they have in the industry"
              name="experienceInIndustryExplanationValue"
              disabled={isSubmitting}
              multiline
              fullWidth
            />
          )}

          <FormControlLabel
            control={<FastField type="checkbox" name="serialEntrepreneursValue" component={Switch} color="secondary" />}
            label="Founders have any past entrepreneurial experiences"
          />

          {serialEntrepreneursValue && (
            <FastField
              component={TextField}
              label="Explain the most relevant founders entrepreneurial experiences"
              name="serialEntrepreneursExplanationValue"
              disabled={isSubmitting}
              multiline
              fullWidth
            />
          )}

          <PerformanceIndicatorEditList />

          <FastField
            component={TextField}
            label="Other traction metrics"
            name="otherTractionMetricsValue"
            disabled={isSubmitting}
            className={classes.field}
            multiline
            fullWidth
          />

          <FormSection title="Funding events">
            <Suspense fallback={<LoadingScreen />}>
              <FundingRoundList organizationId={organizationId} />
            </Suspense>
          </FormSection>

          <AttachmentList />

          <SignalList founders={founders} />

          <OrganizationEventsEditList />

          <FormControlLabel
            control={
              <Field type="checkbox" name="releasedToNetworkManagerValue" component={Switch} color="secondary" />
            }
            label="Released To Network"
          />
        </form>
      </Fade>
    )
  }
}

export type OrganizationDetailsFullEditProps = FormProps<OrganizationFormValues> & OrganizationDetailsEditProps

export type OrganizationDetailsEditProps = {
  organization: Organization
  submitting: boolean
  initialValues: OrganizationFormValues
  onSubmit: (values: OrganizationFormValues) => void
  onCancel: () => void
}

export type OrganizationDetailsEditState = {
  submitted: boolean
}

const styles = ({ spacing }: Theme) =>
  createStyles({
    formControl: {
      marginTop: spacing(2),
      marginBottom: spacing(2),
    },
    submitContainer: {
      position: 'relative',
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: spacing(2),
      marginBottom: spacing(2),
    },
    divider: {
      marginTop: spacing(3),
      marginBottom: spacing(3),
    },
    sectionContainer: {
      marginTop: spacing(2),
      marginBottom: spacing(2),
    },
    sectionItem: {
      marginBottom: spacing(2),
    },
    field: {
      marginBottom: spacing(2),
    },
    extractFromUrl: {
      marginTop: spacing(1),
      marginBottom: spacing(2),
    },
  })

export const organizationValidationSchema = Yup.object().shape({
  signalAssessmentsValues: Yup.array().of(signalAssessmentValidationSchema),
})

export default withForm({
  mapPropsToValues: (props: OrganizationDetailsEditProps) => props.initialValues,
  validationSchema: organizationValidationSchema,
  validateOnBlur: false,
  validateOnChange: false,
  handleSubmit: (
    values: OrganizationFormValues,
    formikBag: FormBag<OrganizationDetailsEditProps, OrganizationFormValues>
  ) => {
    const { onSubmit } = formikBag.props
    const { setSubmitting } = formikBag
    setSubmitting(true)
    onSubmit(values)
  },
})(withStyles(styles)(OrganizationDetailsEdit))
